import { inject, observer } from 'mobx-react';
import React from 'react';
import { EqualHeight, OptionCard, OptionCardContent, OptionCardGroup, Text } from 'telia-front-react';
import MobileBusinessStore from '../../stores/MobileBusinessStore';
import { TranslationsContext } from '@teliaee/telia-translate';
import { replaceDotsAndFormatToTwoDecimalPlacesRoundingUp } from '../../util/PriceUtil';
import { GridProps, PackageOption, PackageOptionGroup } from '../MobileBusinessInterfaces';
import { offerPriceExists, renderRegularPriceWithTooltip } from '../selection/PackagePrice';

export interface Option {
  id: string;
  value: string;
  name: string;
  title: string;
  description: string;
  price: number;
  offerPrice?: number;
  tooltipContent?: JSX.Element;
  inputType: 'radio' | 'checkbox';
}

interface Props {
  group: PackageOptionGroup;
  gridProps?: GridProps;
  mobileBusinessStore?: MobileBusinessStore;
}

const renderOptionCard = (option: Option, priceUnit: string, translate: (key: string) => {}) => {
  const hasOfferPrice = offerPriceExists(option);
  const tooltipId = `tooltip-toggle-mobile-business-form-option-${option.id}`;

  return (
    <OptionCard
      id={option.id}
      key={option.id}
      value={option.value}
      labelText={option.name}
      inputType={option.inputType}
      className="text-center"
      padding="small"
    >
      <OptionCardContent>
        <Text>
          <div>
            <p className="h5 text-bold">{option.title}</p>
            <p className="text-small">{option.description}</p>
          </div>
          <EqualHeight group="price">
            {!!option.price && (
              <strong className="text-small">
                {`${replaceDotsAndFormatToTwoDecimalPlacesRoundingUp(hasOfferPrice ? option?.offerPrice : option?.price)} ${priceUnit}`}
              </strong>
            )}
            {hasOfferPrice && renderRegularPriceWithTooltip(option, tooltipId, translate, priceUnit)}
          </EqualHeight>
        </Text>
      </OptionCardContent>
    </OptionCard>
  );
};

const getInputType = (multipleChoice: boolean) => (multipleChoice ? 'checkbox' : 'radio');

@observer
class PackageOptionCardGroup extends React.Component<Props> {
  static contextType = TranslationsContext;
  declare context: React.ContextType<typeof TranslationsContext>;
  private renderPackageOptionCard = (
    option: PackageOption,
    index: number,
    groupName: string,
    inputType: 'radio' | 'checkbox',
    priceUnit: string
  ): JSX.Element => {
    const translate = this.context.translate;
    return renderOptionCard(
      {
        id: `${groupName}-${index}`,
        value: `${groupName}-${index}`,
        name: translate(option.nameKey),
        title: translate(option.titleKey),
        description: translate(option.descriptionKey),
        price: option.price,
        offerPrice: option.offerPrice,
        tooltipContent: option.tooltipContent,
        inputType
      },
      priceUnit,
      translate
    );
  };

  public render() {
    const translate = this.context.translate;
    const { group, gridProps, mobileBusinessStore } = this.props;
    const priceUnit = translate('mobileBusiness.eurPerMonth');
    return (
      <OptionCardGroup
        inputType={getInputType(group.multipleChoice)}
        name={group.name}
        label={group.name}
        hideLabel={true}
        grid={gridProps?.grid}
        gridColumn={gridProps?.gridColumn}
        onChange={(value) => mobileBusinessStore.setPackageOptions(group.name, value)}
      >
        {group.options.map((option, optionIndex) =>
          this.renderPackageOptionCard(option, optionIndex, group.name, getInputType(group.multipleChoice), priceUnit)
        )}
      </OptionCardGroup>
    );
  }
}

export default inject('mobileBusinessStore')(PackageOptionCardGroup);
